import { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { useStateValue } from 'providers'
import { Button, Icon, Title } from 'components'
import { apiFetch } from 'actions/apiFetch'

const QuestionContainer = styled.div`
  height: 100vh;
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

export const Answer = styled.div`
  ${({ theme: { colors } }) => `
  margin: 1.25rem 0;
  padding: 0.5rem;
  font-size: 1.2rem;
  display: flex;
  justify-content: space-between;
  border-radius: 0.5rem;
  background: ${colors('text', 0.1)};
  svg {
    stroke: currentColor;
  }
  &.selected {
    color: ${colors('success')};
  }
  &.show-results {
    &.correct {
      background: ${colors('success', 0.2)};
      color: ${colors('success')};
    }
    &.selected.incorrect {
      background: ${colors('danger', 0.2)};
      color: ${colors('danger')};
    }
  }
    `}
`

const AnswersContainer = styled.div`
  ${({ theme: { colors } }) => `
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  margin: 2rem 0 5rem;
  &.disabled ${Answer} {
    pointer-events: none;
  }
    `}
`

const ResultMessage = styled.div`
  ${({ theme: { colors } }) => `
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 0.5rem;
  font-size: 1.5rem;
  font-weight: bold;
  &.correct {
    background: ${colors('success', 0.2)};
    color: ${colors('success')};
  }
  &.incorrect {
    background: ${colors('danger', 0.2)};
    color: ${colors('danger')};
  }
    `}
`

const Answers = ({ answers, solution, queueItem, showResults }) => {
  const [selection, setSelection] = useState()
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [{ session: { user } }] = useStateValue()

  // Check if user has already answered this question (from server data)
  const serverHasAnswer = queueItem?.details?.[user?.email] !== undefined
  const userAnswer = queueItem?.details?.[user?.email] || selection

  // Consider answered if either server says so or we just submitted
  const hasAnswered = serverHasAnswer || submitted
  const isCorrect = userAnswer === solution

  // Reset state when question changes
  useEffect(() => {
    setSelection(undefined)
    setSubmitted(false)
    setSubmitting(false)
  }, [queueItem?.id])

  const answerQuestion = () => {
    if (hasAnswered || submitting || !selection) return
    setSubmitting(true)
    setSubmitted(true) // Immediately mark as submitted locally
    apiFetch({
      method: 'PUT',
      path: `/queue_items/${queueItem.id}`,
      payload: { details: { ...queueItem.details, [user.email]: selection } }
    }).then((res) => {
      setSubmitting(false)
      // Socket event will update the queue data - no need to fetch
    }).catch(() => {
      setSubmitting(false)
      setSubmitted(false) // Reset on error
    })
  }

  const isDisabled = hasAnswered || submitting || !selection

  return (
    <AnswersContainer className={hasAnswered ? 'disabled' : ''}>
      <div>
        {answers.map((a) => {
          const isSelected = a === selection || (hasAnswered && a === userAnswer)
          const isCorrectAnswer = a === solution

          return (
            <Answer
              key={a}
              onClick={() => !hasAnswered && (selection === a ? setSelection() : setSelection(a))}
              className={`
                ${isSelected ? 'selected' : ''} 
                ${isCorrectAnswer ? 'correct' : 'incorrect'}
                ${showResults ? 'show-results' : ''}
              `}
            >
              <span dangerouslySetInnerHTML={{ __html: a }}></span>
              {isSelected && <Icon icon="check" />}
              {showResults && isCorrectAnswer && !isSelected && <Icon icon="check" />}
            </Answer>
          )
        })}
      </div>
      {showResults
        ? (
        <ResultMessage className={isCorrect ? 'correct' : 'incorrect'}>
          {isCorrect ? 'Correct!' : 'Incorrect'}
        </ResultMessage>
          )
        : (
        <Button disabled={isDisabled} onPress={answerQuestion}>
          {submitting ? 'Submitting...' : hasAnswered ? 'Waiting for host...' : 'Submit'}
        </Button>
          )}
    </AnswersContainer>
  )
}

// Shuffle array with a seed for consistent ordering
const seededShuffle = (array, seed) => {
  const shuffled = [...array]
  let currentIndex = shuffled.length

  // Simple seeded random using the seed
  const seededRandom = () => {
    seed = (seed * 9301 + 49297) % 233280
    return seed / 233280
  }

  while (currentIndex > 0) {
    const randomIndex = Math.floor(seededRandom() * currentIndex)
    currentIndex--
    ;[shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]]
  }

  return shuffled
}

const getAnswers = (question, questionId) => {
  let answers
  // Use question ID as seed for consistent shuffle per question
  const seed = questionId ? questionId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0

  switch (question.type) {
    case 'multiple':
      answers = [...question.incorrect_answers]
      answers.push(question.correct_answer)
      return seededShuffle(answers, seed)
    default:
      answers = [...question.incorrect_answers]
      answers.push(question.correct_answer)
      return answers
  }
}

export const Question = ({ current, allItems }) => {
  // Track the question we're currently displaying (may differ from active question during transition)
  const [displayedQuestion, setDisplayedQuestion] = useState(current)
  const [showResults, setShowResults] = useState(false)
  const transitionTimeoutRef = useRef(null)

  useEffect(() => {
    // Clear any pending transition
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }

    // If there's no current active question, keep showing the last one (game might be over)
    if (!current) {
      // Check if game is over (no more pending questions)
      const hasPending = allItems?.some(q => q.state === 'pending')
      if (!hasPending && displayedQuestion) {
        // Game is over - show final results
        setShowResults(true)
      }
      return
    }

    // If the active question changed (host clicked next)
    if (current.id !== displayedQuestion?.id) {
      // The previously displayed question is now completed - show results
      setShowResults(true)

      // After 5 seconds, transition to the new question
      transitionTimeoutRef.current = setTimeout(() => {
        setDisplayedQuestion(current)
        setShowResults(false)
      }, 5000)
    }

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [current?.id])

  // Initialize displayed question on first render
  useEffect(() => {
    if (current && !displayedQuestion) {
      setDisplayedQuestion(current)
    }
  }, [current])

  if (!displayedQuestion) return <Title>Game Over</Title>

  // Get the latest data for the displayed question (for updated answer details)
  const questionData = allItems?.find(q => q.id === displayedQuestion.id) || displayedQuestion

  return (
    <QuestionContainer>
      <Title dangerouslySetInnerHTML={{ __html: questionData.details.question }} />
      <Answers
        answers={getAnswers(questionData.details, questionData.id)}
        solution={questionData.details.correct_answer}
        queueItem={questionData}
        showResults={showResults}
      />
    </QuestionContainer>
  )
}
